import type { Plan, PlanItem, PlaylistItem } from '@/types';

/**
 * Builds a playlist from a Plan. The server has already filtered by
 * status='active' and the date window, so we trust it and don't re-filter
 * by date here — the player's clock may be skewed and the plan endpoint
 * is the authoritative gatekeeper.
 *
 * Higher priority (lower number) items get more slots. Within a slot the
 * item's media list rotates sequentially. Same-priority items are
 * interleaved via Fisher-Yates within their band so two campaigns at
 * priority 5 don't always play in the same order.
 */
export function buildPlaylist(
  plan: Plan,
  mediaPaths: Map<string, string>, // mediaId → local path or blob URL
): PlaylistItem[] {
  const playlist: PlaylistItem[] = [];

  const items = [...plan.items].sort((a, b) => a.priority - b.priority);

  for (const item of items) {
    const slots = priorityToSlots(item.priority);
    for (let s = 0; s < slots; s++) {
      for (const media of item.media) {
        // The ready-gate: only items already in the media cache make it
        // into the playlist. Mid-download files are silently skipped
        // this cycle; the next sync's onSyncComplete rebuilds the
        // playlist with them once they're persisted.
        const localPath = mediaPaths.get(media.id);
        if (!localPath) continue;
        playlist.push({
          campaignId: item.campaignId,
          media,
          priority: item.priority,
          localPath,
        });
      }
    }
  }

  return shuffleWithinBands(playlist);
}

/** Priority 1 → 5 slots, priority ≥9 → 1 slot. */
function priorityToSlots(priority: number): number {
  return Math.max(1, 6 - Math.ceil(priority / 2));
}

function shuffleWithinBands(items: PlaylistItem[]): PlaylistItem[] {
  if (items.length <= 1) return items;

  const bands = new Map<number, PlaylistItem[]>();
  for (const item of items) {
    const band = bands.get(item.priority) || [];
    band.push(item);
    bands.set(item.priority, band);
  }

  const result: PlaylistItem[] = [];
  const sortedPriorities = Array.from(bands.keys()).sort((a, b) => a - b);

  for (const priority of sortedPriorities) {
    const band = bands.get(priority)!;
    // Fisher-Yates shuffle
    for (let i = band.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [band[i], band[j]] = [band[j], band[i]];
    }
    result.push(...band);
  }

  return result;
}

/**
 * Returns the next item in the playlist, cycling back to start.
 */
export function getNextItem(
  playlist: PlaylistItem[],
  currentIndex: number,
): { item: PlaylistItem | null; nextIndex: number } {
  if (playlist.length === 0) return { item: null, nextIndex: 0 };

  const nextIndex = (currentIndex + 1) % playlist.length;
  return { item: playlist[nextIndex], nextIndex };
}

// Re-export for callers that previously imported PlanItem-coupled helpers.
export type { PlanItem };
